import { useMediaQuery, useTheme } from "@mui/material";
import { IAddress } from "../../features/checkout/Types";
import envConfig from "../../config/env.config.json";
import _ from "lodash";
import planData from "../../config/data.json";
import { RoutingURLs } from "../../config/RoutingURLs";

export const formatPhoneNumber = (phone: string) => {
  var cleaned = ("" + phone).replace(/\D/g, "");
  var match = cleaned.match(/^(1|)?(\d{3})(\d{3})(\d{4})$/);
  if (match) {
    var intlCode = match[1] ? "+1 " : "";
    return [intlCode, "(", match[2], ") ", match[3], "-", match[4]].join("");
  }
  return null;
};

export const isInvalid = (value: undefined | string | number | object) => {
  return value === undefined || value === null || value === "";
};

export const isInValidAddressInfo = (address: IAddress) => {
  return (
    isInvalid(address.address1) ||
    isInvalid(address.city) ||
    isInvalid(address.state) ||
    isInvalid(address.country) ||
    isInvalid(address.zip)
  );
};

export const getFormattedPhoneNumber = (
  phoneNumber: string,
  isIncludeCountryCode = false
) => {
  let phone = phoneNumber?.replace(/[^\d]/g, "");
  return isIncludeCountryCode ? `+91${phone}` : phone;
};

export const displayPhoneNumberFormat = (phoneNumber: string) => {
  let phone = getFormattedPhoneNumber(phoneNumber);
  return phone
    ?.replace(/\D+/g, "")
    .replace(/(\d{3})(\d{3})(\d{4})/, "($1) $2-$3");
};

export const getNumberWithOrdinal = (n: any) => {
  const s = ["th", "st", "nd", "rd"];
  const v = n % 100;
  return n + (s[(v - 20) % 10] || s[v] || s[0]);
};

export const getStringWithOrdinal = (n: any) => {
  const s = ["Second", "Third", "Fourth", "Fifth", "Sixth"];
  return s[n] + " Line";
};

export const getIsCardExpired = (data: any) => {
  let today, someday;
  let exMonth = data?.dateOfExpiry.substring(0, 2);
  let exYear = data?.dateOfExpiry.substring(2);
  exYear = exYear && `20${exYear}`;
  today = new Date();
  someday = new Date();
  someday.setFullYear(exYear, exMonth, 1);
  if (someday < today) {
    return true;
  }
  return false;
};

export const getIsCardAboutToExpire = (data: any) => {
  const month = parseInt(data?.dateOfExpiry.slice(0, 2), 10);
  const year = parseInt(`20${data?.dateOfExpiry.slice(2)}`, 10);
  const expirationDate = new Date(year, month, 1);
  const currentDate = new Date();
  const difference = expirationDate.getTime() - currentDate.getTime();
  const daysDifference = Math.ceil(difference / (1000 * 60 * 60 * 24));
  if (daysDifference > 0 && daysDifference <= 30) {
    return true;
  } else false;
};

export const getResolutionDetails = () => {
  const theme = useTheme();
  const largeDesktop = useMediaQuery(theme.breakpoints.up("lg"));
  const desktop = useMediaQuery(theme.breakpoints.between("md", "lg"));
  const ipad = useMediaQuery(theme.breakpoints.between("sm", "md"));
  const mobile = useMediaQuery(theme.breakpoints.down("sm"));
  return {
    largeDesktop,
    desktop,
    ipad,
    mobile,
  };
};

export const caclulatePlanCostBasedOnEligibility = (
  actualCost: number,
  discount: number
) => {
  let ssoCustomerInfo: any = localStorage.getItem("ssoCustomerInfo");
  ssoCustomerInfo = JSON.parse(ssoCustomerInfo);
  if (ssoCustomerInfo) {
    if (ssoCustomerInfo?.mobile_discount_qualified) {
      return actualCost - discount;
    } else {
      return actualCost;
    }
  } else {
    if (envConfig.IS_HSI_PLAN || envConfig.IS_PROMO_CODE_CAN_BE_APPLIED) {
      return actualCost - discount || 0;
    } else {
      return actualCost;
    }
  }
};

export const isPromotionalLandingPage = (): boolean => {
  return localStorage.getItem("isPromotionalLandingPage") === "true";
};

export const hasValidSourceUrl = () => {
  const sourceUrl = localStorage.getItem("sourceUrl")?.trim();
  if (sourceUrl) {
    try {
      new URL(sourceUrl, window.location.origin);
      return true;
    } catch {
      return false;
    }
  }
  return false;
};

export const setCartHeader = (value: boolean) => {
  localStorage.setItem("isCartHeader", JSON.stringify(value));
};

export const isCartHeader = () => {
  return localStorage.getItem("isCartHeader") === "true";
};

export const getValidSourceUrl = () => {
  return localStorage.getItem("sourceUrl")?.trim() || RoutingURLs.home;
};

export const isLandingPage = (): boolean => {
  const landingPageInfo = JSON.parse(
    localStorage.getItem("landingPageInfo") || JSON.stringify("")
  );
  return landingPageInfo?.isLandingPage;
};

export const landingPageInfo = (): any => {
  const landingPageInfo = JSON.parse(
    localStorage.getItem("landingPageInfo") || JSON.stringify("")
  );
  return landingPageInfo;
};
export const updateSvgFillColor = (svgCode: string, color: string) => {
  if (svgCode) {
    return svgCode.replace(/fill="[^"]*"/g, `fill="${color}"`);
  } else {
    return svgCode;
  }
};

export const setPromotionalLandingPage = (value: boolean): void => {
  localStorage.setItem("isPromotionalLandingPage", JSON.stringify(value));
};

export const promotionalLandingPagePlanName = (): any => {
  // Enum-like structure to map plans to URL paths
  const pathnames = {
    lpByTheGig: RoutingURLs?.lpByTheGig,
    lpEssential: RoutingURLs?.lpEssential,
    lpUnlimited: RoutingURLs?.lpUnlimited,
    lpUnlimitedPlus: RoutingURLs?.lpUnlimitedPlus,
  };

  const pathname = landingPageInfo()?.route || window.location.pathname; // Current URL path

  // Helper function to match the pathname with the plan name
  const getPlanNameFromPath = (): any => {
    switch (pathname) {
      case pathnames.lpByTheGig:
        return "By the Gig";
      case pathnames.lpEssential:
        return "Essentials";
      case pathnames.lpUnlimited:
        return "Unlimited";
      case pathnames.lpUnlimitedPlus:
        return "Unlimited +";
      default:
        return "All Plan";
    }
  };

  const matchedPlanName = getPlanNameFromPath(); // Get the plan name from path

  // If matched plan name exists, find it in the planData array and return it
  const matchedPlan = planData.data.find(
    (plan: any) => plan.name === matchedPlanName
  );

  return matchedPlan
    ? { name: matchedPlan.name, data: matchedPlan }
    : { name: "All Plan", data: planData }; // Return the plan name if found, otherwise undefined
};

export const getFontFamily = (data: any) => {
  switch (data) {
    case 1:
      return "var(--font_family_Bold)";
    case 2:
      return "var(--font_family_Semibold)";
    case 3:
      return "var(--font_family_Medium)";
    case 4:
      return "var(--font_family_Regular)";
    default:
      return data;
  }
};

//Pass fontFamily as 1,2,3,4 for Bold, Semibold, Medium and regular.
//Pass fontSize like "14px" or {xs:"10px", md:"12px"}
//Pass color like "primary_color"
export const renderTextStyles = (
  fontFamily: string | number,
  fontSize: any,
  color: string,
  _lineHeight?: any,
  _fontWeight?: string,
  _letterSpacing?: any
): any => {
  return {
    fontFamily: getFontFamily(fontFamily),
    fontSize: _.isString(fontSize) ? fontSize : { ...fontSize },
    color: color === "inherit" ? color : `var(--${color})`,
    ...(_lineHeight && {
      lineHeight: _.isString(_lineHeight) ? _lineHeight : { ..._lineHeight },
    }),
    ...(_fontWeight && {
      fontWeight: _fontWeight,
    }),
    ...(_letterSpacing && {
      letterSpacing: _.isString(_letterSpacing)
        ? _letterSpacing
        : { ..._letterSpacing },
    }),
  };
};

export function capitalize(str: string) {
  return str?.replace(/\b\w+/g, (word) => {
    if (word.match(/[A-Z]/)) {
      return word.charAt(0) + word.slice(1).toLowerCase();
    } else {
      return word.charAt(0).toUpperCase() + word.slice(1).toLowerCase();
    }
  });
}
